import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Col, message, Row, Table } from "antd";
import { EditOutlined } from "@ant-design/icons";

import {
  constructDisplayPrice,
  constructDisplayPercentage,
  constructShortenedFinancialNumber,
} from "utils/general";
import { patchAdviseeInvestmentPortfolio } from "apis/advisee";

import InfoCard from "components/InfoCard/InfoCard";
import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";

import ManageGoalModal from "./components/ManageGoalModal/ManageGoalModal";
import ManageAssumptionModal from "./components/ManageAssumptionModal/ManageAssumptionModal";

import {
  DescriptionForLabel,
  FinancialValueText,
  Label,
  SummaryValueText,
} from "./Summary.styles";

import Locale from "react-intl-universal";

const constructAssumptionsDataSource = (assumptionInfo) => [
  {
    label: Locale.get("investmentPortfolio.inflationRate"),
    value: constructDisplayPercentage(assumptionInfo.inflation),
  },
  {
    label: Locale.get("investmentPortfolio.epfRate"),
    value: constructDisplayPercentage(assumptionInfo.epf),
  },
  {
    label: Locale.get("investmentPortfolio.fdRate"),
    value: constructDisplayPercentage(assumptionInfo.fd),
  },
  {
    label: Locale.get("investmentPortfolio.unitTrustRate"),
    value: constructDisplayPercentage(assumptionInfo.unitTrust),
  },
  {
    label: Locale.get("investmentPortfolio.shareRate"),
    value: constructDisplayPercentage(assumptionInfo.share),
  },
  {
    label: Locale.get("investmentPortfolio.propertyRate"),
    value: constructDisplayPercentage(assumptionInfo.property),
  },
  {
    label: Locale.get("investmentPortfolio.salaryIncreaseRate"),
    value: constructDisplayPercentage(assumptionInfo.salaryIncrease),
  },
  {
    label: Locale.get("investmentPortfolio.bSavingsCurrent"),
    value: constructDisplayPercentage(assumptionInfo.bankSavings),
  },
];

const constructSummaryDataSource = (summaryInfo) => [
  {
    label: Locale.get("investmentPortfolio.tProjectedNetValue"),
    value: constructDisplayPrice(summaryInfo.projectedNet),
  },
  {
    label: Locale.get("investmentPortfolio.shortage"),
    value: constructDisplayPrice(summaryInfo.shortage),
  },
  {
    label: Locale.get("investmentPortfolio.availableFunds"),
    value: summaryInfo.monthlyAvailableFunds,
    // value: constructDisplayPrice(summaryInfo.monthlyAvailableFunds)
  },
];

const getInsuranceTableColumns = (title, setShowManageAssumptionModal) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    title: () => (
      <Button
        type="primary"
        icon={<EditOutlined />}
        style={{ float: "right" }}
        onClick={() => setShowManageAssumptionModal(true)}
      >
        {Locale.get("investmentPortfolio.manageAssumption")}
      </Button>
    ),
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const SummaryCard = ({ title, value, isText }) => {
  return (
    <Col span={12} xl={8}>
      <InfoCard title={title}>
        <SummaryValueText isText={isText}>{value}</SummaryValueText>
      </InfoCard>
    </Col>
  );
};

const Summary = ({
  goalSetting = {},
  assumptionInfo = {},
  summaryInfo = {},
  showManageGoalModal,
  setShowManageGoalModal,
}) => {
  const { id: adviseeId } = useParams();
  const [showManageAssumptionModal, setShowManageAssumptionModal] = useState(
    false
  );

  const updateInvestmentPortfolio = (payload, type) => {
    patchAdviseeInvestmentPortfolio(adviseeId, type, payload)
      .then(() => {
        message.success(Locale.get("investmentPortfolio.updateSuccess"));
      })
      .catch(() => {
        message.error(Locale.get("investmentPortfolio.updateErrorMsg"));
      });
  };

  const handleGoalModalOk = (goals) => {
    updateInvestmentPortfolio(goals, "goal");
    setShowManageGoalModal(false);
  };

  const handleAssumptionModalOk = (assumptions) => {
    updateInvestmentPortfolio(assumptions, "assumption");
    setShowManageAssumptionModal(false);
  };

  return (
    <>
      <SectionContainer>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title>{Locale.get("investmentPortfolio.titleInvestment")}</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              style={{ float: "right" }}
              onClick={() => setShowManageGoalModal(true)}
            >
              {Locale.get("investmentPortfolio.manageGoalSetting")}
            </Button>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <SummaryCard
            title={Locale.get("investmentPortfolio.goalDesc")}
            value={goalSetting.description || "-"}
            isText
          ></SummaryCard>
          <SummaryCard
            title={Locale.get("investmentPortfolio.goalAmount")}
            value={constructShortenedFinancialNumber(
              goalSetting.goalAmt,
              false
            )}
          ></SummaryCard>
          <SummaryCard
            title={Locale.get("investmentPortfolio.yearToAchieve")}
            value={goalSetting.yearsToAchieve || 0}
          ></SummaryCard>
        </Row>
      </SectionContainer>
      <SectionContainer>
        <Title marginBottom="24px">
          {Locale.get("investmentPortfolio.summaryInvestmentPortfolio")}
        </Title>
        <Table
          dataSource={constructSummaryDataSource(summaryInfo)}
          columns={getInsuranceTableColumns()}
          pagination={false}
          showHeader={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
        />
      </SectionContainer>
      <SectionContainer>
        <Table
          dataSource={constructAssumptionsDataSource(assumptionInfo)}
          columns={getInsuranceTableColumns(
            Locale.get("investmentPortfolio.assumption"),
            setShowManageAssumptionModal
          )}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
        />
      </SectionContainer>
      {showManageGoalModal && (
        <ManageGoalModal
          defaultValues={goalSetting}
          visible={showManageGoalModal}
          onCancel={() => setShowManageGoalModal(false)}
          onOk={handleGoalModalOk}
        />
      )}
      {showManageAssumptionModal && (
        <ManageAssumptionModal
          defaultValues={assumptionInfo}
          visible={showManageAssumptionModal}
          onCancel={() => setShowManageAssumptionModal(false)}
          onOk={handleAssumptionModalOk}
        />
      )}
    </>
  );
};

export default Summary;
